import { 
  Trophy, Users, Plus, AlertCircle, ArrowUp, ArrowDown, Trash2
} from "lucide-react";

const LEVEL_EMOJIS = ['🌱', '🧭', '☕', '✍️', '⭐', '🏆', '👑'];

const BADGE_EMOJIS = {
  first_review: '🎉',
  five_reviews: '📝',
  ten_reviews: '✨',
  early_adopter: '🚀',
  helpful_10: '👍',
  streak_7: '🔥',
  yearly_champion: '🏆'
};

export default function AdminContributorsTab({
  contributors,
  filteredContributors,
  contributorStats,
  sortConfig,
  handleSort,
  onAddContributor,
  onDeleteContributor
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Community Contributors
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            View all contributors, their points, badges, and activity. 
            <a href="/policies" className="text-brand hover:underline ml-1">Privacy Policy</a>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{contributorStats.total} total, {contributorStats.banned} banned</span>
          <button
            onClick={onAddContributor}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            data-testid="add-contributor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Contributor
          </button>
          <a 
            href="/moderation" 
            className="px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand-dark transition-colors"
          >
            Review Moderation
          </a>
        </div>
      </div>
      
      {/* Privacy Notice */}
      <div className="p-3 bg-blue-50 border-b border-blue-100">
        <p className="text-xs text-blue-700 flex items-center gap-1.5">
          <AlertCircle className="w-4 h-4" />
          Contributor data is protected under our <a href="/privacy" className="underline">Privacy Policy</a>. 
          Email addresses shown here are for moderation purposes only and must not be shared externally.
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contributors', 'name')}
              >
                <div className="flex items-center gap-1">
                  Contributor
                  {sortConfig.contributors?.field === 'name' && (
                    sortConfig.contributors.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left">Email (Protected)</th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contributors', 'total_xp')}
              >
                <div className="flex items-center justify-center gap-1">
                  Level & XP
                  {sortConfig.contributors?.field === 'total_xp' && (
                    sortConfig.contributors.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-center">Badges</th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contributors', 'review_count')}
              >
                <div className="flex items-center justify-center gap-1">
                  Reviews
                  {sortConfig.contributors?.field === 'review_count' && (
                    sortConfig.contributors.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contributors', 'helpful_votes')}
              >
                <div className="flex items-center justify-center gap-1">
                  Helpful Votes
                  {sortConfig.contributors?.field === 'helpful_votes' && (
                    sortConfig.contributors.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Violations</th>
              <th 
                className="px-4 py-3 text-right cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contributors', 'created_at')}
              >
                <div className="flex items-center justify-end gap-1">
                  Joined
                  {sortConfig.contributors?.field === 'created_at' && (
                    sortConfig.contributors.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredContributors.map((contrib) => (
              <tr key={contrib.id} className={`hover:bg-gray-50 ${contrib.is_banned ? 'bg-red-50/50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand to-brand-dark flex items-center justify-center text-white font-medium text-sm">
                      {contrib.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{contrib.name}</p>
                      <p className="text-xs text-gray-500">{contrib.level_name}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-600 font-mono">{contrib.email}</span>
                    <span className="text-xs text-gray-400">(protected)</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex flex-col items-center">
                    <span className="text-lg">{LEVEL_EMOJIS[contrib.level - 1] || '🌱'}</span>
                    <span className="text-sm font-semibold text-gray-900">{contrib.xp?.toLocaleString()} XP</span>
                    <span className="text-xs text-gray-500">Level {contrib.level}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex flex-wrap justify-center gap-0.5" title={contrib.badges?.join(', ')}>
                    {contrib.badges?.slice(0, 5).map((badge) => (
                      <span key={badge} className="text-sm">
                        {BADGE_EMOJIS[badge] || '🏅'}
                      </span>
                    ))}
                    {contrib.badges?.length > 5 && (
                      <span className="text-xs text-gray-400">+{contrib.badges.length - 5}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{contrib.badges?.length || 0} total</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-medium text-gray-900">{contrib.stats?.total_reviews || 0}</span>
                    <span className="text-xs text-green-600">{contrib.stats?.approved_reviews || 0} approved</span>
                    {contrib.stats?.removed_reviews > 0 && (
                      <span className="text-xs text-red-600">{contrib.stats.removed_reviews} removed</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="font-medium text-gray-900">{contrib.stats?.helpful_votes_received || 0}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  {contrib.is_banned ? (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                      Banned
                    </span>
                  ) : contrib.trusted_reviewer ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Trusted
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {contrib.violations?.length > 0 ? (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                      {contrib.violations.length} violation{contrib.violations.length > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-500">
                  {new Date(contrib.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onDeleteContributor && onDeleteContributor(contrib)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete contributor"
                    data-testid={`delete-contributor-${contrib.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {contributors.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
          No contributors yet. Contributors are created when users submit reviews with their email.
        </div>
      )}
    </div>
  );
}
